import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { HeroBackground, PageHeader } from '../components';
import { apiService } from '../services/api';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await apiService.sendContactEmail(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (err: any) {
      setError(
        err.response?.data?.detail || t('contact.error_generic')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero header cropped to 1/3 */}
      <HeroBackground size='third' />

      {/* Page Title */}
      <PageHeader
        title={t('nav.contact')}
        subtitle={t('subtitles.contact')}
      />

      <div className='max-w-4xl mx-auto px-5 py-4'>
        <div className='bg-church-light rounded-lg shadow-lg p-5'>
          <div className='mb-3'>
            <p className='text-gray-700 leading-relaxed text-sm'>
              {t('contact.intro')}
            </p>
          </div>

          {success && (
            <div className='mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2'>
              <CheckCircle className='w-5 h-5 text-green-600 shrink-0 mt-0.5' />
              <p className='text-green-800 text-sm'>
                {t('contact.success')}
              </p>
            </div>
          )}

          {error && (
            <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-red-800 text-sm'>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className='space-y-3'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div>
                <label
                  htmlFor='name'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  {t('contact.name_label')} <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className='w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-brown focus:border-transparent'
                  placeholder={t('contact.name_placeholder')}
                />
              </div>

              <div>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  {t('contact.email_label')} <span className='text-red-500'>*</span>
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className='w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-brown focus:border-transparent'
                  placeholder={t('contact.email_placeholder')}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor='subject'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                {t('contact.subject_label')} <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                id='subject'
                name='subject'
                required
                value={formData.subject}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-brown focus:border-transparent'
                placeholder={t('contact.subject_placeholder')}
              />
            </div>

            <div>
              <label
                htmlFor='message'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                {t('contact.message_label')} <span className='text-red-500'>*</span>
              </label>
              <textarea
                id='message'
                name='message'
                required
                rows={3}
                value={formData.message}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-brown focus:border-transparent resize-vertical'
                placeholder={t('contact.message_placeholder')}
              />
            </div>

            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={loading}
                className='bg-church-brown text-white px-8 py-3 rounded-md hover:bg-church-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? t('contact.sending') : t('contact.send')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
